import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { BusinessCategory, detectBusinessCategory } from '@/lib/integrations';
import { useGooglePlacesTypes } from '@/hooks/business/useGooglePlacesTypes';

interface BusinessStats {
  employeeCount: number;
  firstHireDate: string | null;
  loyaltyProgramsCount: number;
  jobPostingsCount: number;
  totalShiftsCount: number;
  thisMonthShiftsCount: number;
  lastReviewDate: string | null;
  daysActive: number;
  googlePlacesTypes: string[];
  businessCategory: BusinessCategory | null;
  qrCodesCount: number;
}

export const useBusinessStats = (businessId: string | null, createdAt: string | null, googlePlaceId?: string | null) => {
  // Use optimized Google Places types hook with TanStack Query
  const { data: googleTypesData } = useGooglePlacesTypes(businessId, googlePlaceId);

  const [stats, setStats] = useState<BusinessStats>({
    employeeCount: 0,
    firstHireDate: null,
    loyaltyProgramsCount: 0,
    jobPostingsCount: 0,
    totalShiftsCount: 0,
    thisMonthShiftsCount: 0,
    lastReviewDate: null,
    daysActive: 0,
    googlePlacesTypes: [],
    businessCategory: null,
    qrCodesCount: 0,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!businessId) return;

    const fetchStats = async () => {
      setLoading(true);
      try {
        // Calculate days active
        const daysActive = createdAt 
          ? Math.floor((new Date().getTime() - new Date(createdAt).getTime()) / (1000 * 60 * 60 * 24))
          : 0;

        // Fetch employee count and first hire date
        const { data: employees, error: employeesError } = await supabase
          .from('employees')
          .select('created_at')
          .eq('business_id', businessId)
          .order('created_at', { ascending: true });

        if (employeesError) throw employeesError;

        const employeeCount = employees?.length || 0;
        const firstHireDate = employees && employees.length > 0 ? employees[0].created_at : null;

        // Fetch loyalty programs count
        const { count: loyaltyCount, error: loyaltyError } = await supabase
          .from('loyalty_programs')
          .select('*', { count: 'exact', head: true })
          .eq('business_id', businessId)
          .eq('is_active', true);

        if (loyaltyError) throw loyaltyError;

        // Fetch job postings count
        const { count: jobPostingsCount, error: jobPostingsError } = await supabase
          .from('job_postings')
          .select('*', { count: 'exact', head: true })
          .eq('business_id', businessId);

        if (jobPostingsError) throw jobPostingsError;

        // Fetch total shifts count with error handling
        let totalShiftsCount = 0;
        let thisMonthShiftsCount = 0;

        try {
          const { count, error: totalShiftsError } = await supabase
            .from('shifts')
            .select('*', { count: 'exact', head: true })
            .eq('business_id', businessId);

          if (totalShiftsError) {
            console.warn('Error fetching total shifts count:', totalShiftsError);
          } else {
            totalShiftsCount = count || 0;
          }
        } catch (error) {
          console.warn('Failed to fetch total shifts count:', error);
        }

        // Fetch this month's shifts count with error handling
        try {
          const startOfMonth = new Date();
          startOfMonth.setDate(1);
          startOfMonth.setHours(0, 0, 0, 0);

          const { count, error: thisMonthShiftsError } = await supabase
            .from('shifts')
            .select('*', { count: 'exact', head: true })
            .eq('business_id', businessId)
            .gte('start_time', startOfMonth.toISOString());

          if (thisMonthShiftsError) {
            console.warn('Error fetching this month shifts count:', thisMonthShiftsError);
          } else {
            thisMonthShiftsCount = count || 0;
          }
        } catch (error) {
          console.warn('Failed to fetch this month shifts count:', error);
        }

        // Fetch last review date
        const { data: lastReview, error: reviewError } = await supabase
          .from('reviews')
          .select('created_at')
          .eq('business_id', businessId)
          .order('created_at', { ascending: false })
          .limit(1);

        if (reviewError) throw reviewError;

        const lastReviewDate = lastReview && lastReview.length > 0 ? lastReview[0].created_at : null;

        // Fetch QR codes count for this business
        const { count: qrCodesCount, error: qrCodesError } = await supabase
          .from('qr_codes')
          .select('*', { count: 'exact', head: true })
          .eq('business_id', businessId);

        if (qrCodesError) throw qrCodesError;

        // Get Google Places types from optimized hook (with TanStack Query deduplication)
        const googlePlacesTypes = googleTypesData?.types || [];
        const businessCategory = googlePlacesTypes.length > 0
          ? detectBusinessCategory(googlePlacesTypes)
          : null;

        setStats({
          employeeCount,
          firstHireDate,
          loyaltyProgramsCount: loyaltyCount || 0,
          jobPostingsCount: jobPostingsCount || 0,
          totalShiftsCount,
          thisMonthShiftsCount,
          lastReviewDate,
          daysActive,
          googlePlacesTypes,
          businessCategory,
          qrCodesCount: qrCodesCount || 0,
        });
      } catch (error) {
        console.error('Error fetching business stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [businessId, createdAt, googlePlaceId, googleTypesData]);

  return { stats, loading };
};